<script lang="ts">
import CodexPulseLoader from '@/components/utils/CodexPulseLoader.vue'
import { ApplyValidationRules } from '@/lib/validationRules'
import { CodexDropdownOption } from '@/types'
import { PropType, ref } from 'vue'
import VueSelect from 'vue-select'

export default {
  name: 'CodexDropdown',
  components: { VueSelect, CodexPulseLoader },
  props: {
    options: {
      type: Array as PropType<CodexDropdownOption[] | string[]>,
      default: null
    },
    // eslint-disable-next-line vue/require-prop-types
    modelValue: {
      default: null
    },
    labelText: {
      type: String,
      default: ''
    },
    labelRequired: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: true
    },
    clearable: {
      type: Boolean,
      default: false
    },
    filterable: {
      type: Boolean,
      default: true
    },
    asyncSearch: {
      type: Function,
      default: () => () => null
    },
    useNoBorderStyles: {
      type: Boolean,
      default: false
    },
    isInfiniteScroll: {
      type: Boolean,
      default: false
    },
    totalPages: {
      type: Number,
      default: 0
    },
    asyncLoadMore: {
      type: Function as PropType<
        (
          page: number,
          search: string
        ) => Promise<{
          items: CodexDropdownOption[] | string[]
          page: number
          totalItems: number
        }>
      >,
      default: () => () => null
    },
    validationKey: {
      type: String,
      default: null
    },
    validationRules: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    selectable: {
      type: Function,
      default: () => () => true
    },
    zeroStateMessage: {
      type: String,
      default: null
    }
  },
  emits: ['blur', 'validated', 'update:modelValue', 'input', 'deselect', 'cleared'],
  setup() {
    const errors = ref('')
    const isDirty = ref(false)

    return {
      errors,
      isDirty
    }
  },
  data() {
    const hasFocus = false
    const observer: IntersectionObserver | null = null
    const currentPage = 1
    const search = ''

    return {
      hasFocus,
      observer,
      currentPage,
      search
    }
  },
  computed: {
    hasNextPage(): boolean {
      return this.isInfiniteScroll && this.currentPage < this.totalPages
    },
    value: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      }
    }
  },
  watch: {
    value: {
      handler() {
        this.validateInstance()
      },
      immediate: true
    },
    validationRules: {
      handler() {
        this.validateInstance()
      },
      immediate: false
    }
  },
  mounted() {
    this.validateInstance()
    if (this.isInfiniteScroll) {
      this.observer = new IntersectionObserver(this.infiniteScrollHandler)
    }
  },
  methods: {
    async validateInstance() {
      if (typeof this.validationRules === 'string') {
        // Race condition if there are multiple drop downs on one page that interact
        // to change if the drop down is valid causing an invalid (or valid) value to
        // validate incorrectly. Pausing 200 ms clears up the condition and make the rules
        // apply correctly.
        await new Promise((r) => setTimeout(r, 200))

        const errArray = ApplyValidationRules(this.validationRules, this.value, this.name)
        this.errors = errArray.filter((err) => err.length > 0).join(' ')
        this.$emit('validated', { errors: this.errors, key: this.validationKey || this.name })
      }
    },
    inputChanged(newValue): void {
      this.$emit('input', newValue)
      this.value = newValue
      this.isDirty = true
    },
    receivedFocus(): void {
      this.hasFocus = true
    },
    lostFocus(): void {
      this.hasFocus = false
      this.$emit('blur')
      this.isDirty = true
    },
    searchHandler(search, loading) {
      this.currentPage = 1
      return this.asyncSearch(search, loading)
    },
    async infiniteScrollHandler(entries) {
      if (this.hasNextPage && entries?.length > 0 && entries[0].isIntersecting) {
        this.currentPage++
        const target = entries[0].target
        const ul = target.offsetParent
        const scrollTop = target.offsetParent.scrollTop
        const { items } = await this.asyncLoadMore(this.currentPage, this.search)
        if (items?.length > 0) {
          this.$forceUpdate()
          await this.$nextTick()
          ul.scrollTop = scrollTop
        }
      }
    },
    async onOpen() {
      if (this.isInfiniteScroll && this.hasNextPage) {
        await this.$nextTick()
        this.observer.observe(this.$refs.loading as Element)
      }
    },
    onClose() {
      if (this.isInfiniteScroll) {
        this.observer.disconnect()
      }
    }
  }
}
</script>

<template>
  <div :class="useNoBorderStyles && $style.noBorderDropdown">
    <label :for="name">
      <div :class="$style.displayInline" v-html="labelText" />
      <span v-if="labelRequired" :class="$style.required">required</span></label
    >
    <div :class="[$style.codexDropdown, 'codexDropdown', hasFocus ? $style.hasFocus : '']">
      <VueSelect
        :id="name"
        :model-value="value"
        :placeholder="placeholder"
        :searchable="searchable"
        :filterable="filterable"
        :options="options"
        :clearable="clearable"
        :disabled="disabled"
        :loading="loading"
        :selectable="selectable"
        :name="name"
        data-testid="dropdown__select"
        @search="searchHandler"
        @option:selected="inputChanged"
        @search:focus="receivedFocus"
        @search:blur="lostFocus"
        @keypress.enter="(event) => event.preventDefault()"
        @open="onOpen"
        @close="onClose"
        @update:model-value="
          (v) => {
            if (!v || (Array.isArray(v) && v.length === 0)) {
              $emit('cleared')
            }
          }
        "
      >
        <template #spinner="{ loading }">
          <CodexPulseLoader
            v-if="loading && hasFocus"
            class="pulseLoader"
            :size="'0.5em'"
            :margin="'0'"
          />
          <span v-else />
        </template>
        <template v-if="isInfiniteScroll" #list-footer>
          <li v-show="hasNextPage" ref="loading" class="loader">Loading more ...</li>
        </template>
        <template v-if="zeroStateMessage" #no-options>
          {{ zeroStateMessage }}
        </template>
      </VueSelect>
    </div>
    <p class="errorMessage">
      {{ isDirty && errors ? errors : '&nbsp;' }}
    </p>
  </div>
</template>

<style module>
.displayInline {
  display: inline;
}

.required {
  background-color: #eff2f4 !important;
  border: solid 1px #c7cacb !important;
  border-radius: 4px !important;
  padding: 0.2rem 0.4rem !important;
  margin-left: 0.5rem !important;
  font-weight: 400 !important;
  font-size: 0.8rem !important;
}
.codexDropdown {
  background-color: white;
}

.codexDropdown.hasFocus {
  outline: 1px dashed #626a71;
  outline-offset: 2px;
}

.codexDropdown :global .v-select,
.codexDropdown :global .v-select * {
  box-sizing: border-box;
}

.codexDropdown :global .v-select {
  position: relative;
  font-family: inherit;
}

.codexDropdown :global .vs__dropdown-toggle:hover,
.codexDropdown :global .vs__dropdown-toggle *:hover,
.codexDropdown :global .vs__dropdown-menu:hover {
  cursor: pointer;
}

.codexDropdown :global .vs__dropdown-toggle {
  display: flex;
  appearance: none;
  padding: 4px 0 8px;
  background: none;
  border: 1px solid #626a71;
}

.codexDropdown :global .vs__dropdown-toggle:focus {
  outline: 1px dashed #626a71;
  outline-offset: 2px;
}

.codexDropdown :global .vs--single .vs__selected {
  display: flex;
  align-items: center;
  line-height: 1.4;
  margin: 4px 2px 0 0;
  padding: 0 0.5em;
}

.codexDropdown :global .vs--searching .vs__selected {
  visibility: hidden;
}

.codexDropdown :global .vs__search,
.codexDropdown :global .vs__search:focus {
  line-height: 1.4;
  border: none;
  outline: none;
  margin: 6px 0 0;
  padding: 0 0.5em;
  width: 100%;
  position: absolute;
  background: transparent;
}

.codexDropdown :global .vs__actions {
  align-items: center;
  padding: 4px 6px 0 3px;
  text-align: right;
  min-width: 33px;
  min-height: 30px;
  display: flex;
}

.codexDropdown :global .vs__actions button {
  margin: 0;
  background: none;
  border: none;
}

.codexDropdown :global .vs__selected-options {
  display: flex;
  flex: 1 100%;
  flex-wrap: wrap;
  padding: 0 2px;
  position: relative;
  min-width: 92%;
}

.codexDropdown :global .vs__open-indicator {
  margin-right: 6px;
  transform: scale(1);
  transition: transform 0.15s cubic-bezier(1, -0.115, 0.975, 0.855);
}

.codexDropdown :global .clear {
  visibility: hidden;
}

.codexDropdown :global .vs--open .vs__open-indicator {
  transform: rotate(180deg) scale(1);
}

.codexDropdown :global .vs__dropdown-menu {
  display: block;
  position: absolute;
  top: 42px;
  left: 0;
  z-index: 1000;
  padding: 0;
  margin: 0;
  width: 100%;
  max-height: 350px;
  min-width: 160px;
  overflow-y: auto;
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.15);
  text-align: left;
  list-style: none;
  background: #fff;
}

.codexDropdown :global .vs__no-options {
  text-align: center;
}

.codexDropdown :global .vs__dropdown-option {
  line-height: 1.5;
  display: block;
  padding: 10px 0.75em;
  clear: both;
  white-space: nowrap;
}

.codexDropdown :global .vs__dropdown-option--highlight {
  background: #f2f2f2;
}

.codexDropdown :global .vs--loading :global .vs__open-indicator {
  display: none;
}

.codexDropdown :global .vs--disabled {
  background-color: #f2f2f2;
}

.codexDropdown :global .vSpinner {
  display: inline-block;
  margin: 0;
}

/* No Border Styles */
.noBorderDropdown {
  font-family: 'Gilroy', sans-serif;
  font-size: 20px;
  text-align: center;
}

.noBorderDropdown > div {
  max-width: 290px;
  margin: 0 auto;
}

.noBorderDropdown > label {
  margin-right: 1%;
}

.noBorderDropdown :global .vs__dropdown-toggle {
  max-width: fit-content;
  margin: 0 auto;
  border: none;
}

.noBorderDropdown :global .vs__selected {
  color: #0d4760;
  border-bottom: solid 4px #0d4760;
}

.noBorderDropdown :global .vs__selected-options {
  justify-content: center;
}

.noBorderDropdown .codexDropdown.hasFocus {
  outline: none;
}

.noBorderDropdown .codexDropdown.hasFocus :global .vs__selected {
  outline: 1px dashed #626a71;
  outline-offset: 6px;
}
</style>
